import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import MatchNightRsvpDialog from "../match-night/MatchNightRsvpDialog";
import { formatNightDateLabel } from "../match-night/cetTime";
import { pickNextNightForPreferredRegion } from "../match-night/preferredMatchNight";
import useCountdown from "../match-night/useCountdown";
import useMatchNights from "../match-night/useMatchNights";
import useMyMatchNights from "../match-night/useMyMatchNights";

const STORAGE_KEY = "ouro_match_night_promo_dismissed";
const ACCENT = "#E8A33D";
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

function readDismissedNightId() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistDismissedNightId(nightId) {
  try {
    localStorage.setItem(STORAGE_KEY, nightId);
  } catch {
    // localStorage unavailable
  }
}

function clearDismissedNightId() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
}

function isUpcoming(item) {
  const scheduledAt = new Date(item?.night?.scheduledAt);
  return scheduledAt instanceof Date && !Number.isNaN(scheduledAt.getTime()) && scheduledAt > new Date();
}

function getStatusLabel(status) {
  if (status === "waitlisted") return "Waitlisted";
  if (status === "pending_verification") return "Pending verification";
  return "You're in";
}

function getCountsLine(night) {
  const base =
    night.confirmedCount >= night.minToFire
      ? `${night.confirmedCount} confirmed`
      : `${night.confirmedCount}/${night.minToFire} confirmed`;
  return night.pendingCount > 0 ? `${base}, ${night.pendingCount} in review` : base;
}

export default function LobbyMatchNightBanner({ country } = {}) {
  const { myRsvps, loading: myRsvpsLoading, basePath, refetch: refetchMyRsvps } = useMyMatchNights();
  const { nights, preferredRegion, loading: nightsLoading, refetch: refetchNights } = useMatchNights({
    country,
  });
  const [dismissedNightId, setDismissedNightId] = React.useState(readDismissedNightId);
  const [rsvpDialogNight, setRsvpDialogNight] = React.useState(null);
  const [cancellingRsvpId, setCancellingRsvpId] = React.useState(null);
  const [cancelError, setCancelError] = React.useState(null);

  const refresh = React.useCallback(() => {
    refetchMyRsvps();
    refetchNights();
  }, [refetchMyRsvps, refetchNights]);

  React.useEffect(() => {
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  const upcomingRsvps = React.useMemo(
    () =>
      myRsvps
        .filter(isUpcoming)
        .sort((a, b) => new Date(a.night.scheduledAt) - new Date(b.night.scheduledAt)),
    [myRsvps]
  );
  const nextRsvp = upcomingRsvps[0] || null;
  const nextNight = pickNextNightForPreferredRegion(nights, preferredRegion);
  const countdown = useCountdown(new Date(nextRsvp?.night?.scheduledAt || nextNight?.scheduledAt || null));

  const handleRsvpSuccess = React.useCallback(() => {
    setRsvpDialogNight(null);
    clearDismissedNightId();
    setDismissedNightId(null);
    refresh();
  }, [refresh]);

  const handleCancel = React.useCallback(async () => {
    if (!nextRsvp?.rsvp?.id) return;
    setCancellingRsvpId(nextRsvp.rsvp.id);
    setCancelError(null);

    try {
      const response = await fetch(`${basePath}/api/v1/match-nights/rsvps/${nextRsvp.rsvp.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || "Could not cancel RSVP.");
      }
      refresh();
    } catch (error) {
      setCancelError(error?.message || "Could not cancel RSVP.");
    } finally {
      setCancellingRsvpId(null);
    }
  }, [basePath, nextRsvp, refresh]);

  if (myRsvpsLoading || nightsLoading) return null;

  if (nextRsvp) {
    const { night, rsvp } = nextRsvp;

    return (
      <Fade in timeout={250}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.25, md: 1.4 },
            mb: 1,
            borderLeftWidth: 1,
            borderLeftStyle: "solid",
            borderLeftColor: ACCENT,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "rgba(255,255,255,0.92)" }}>
                Match night: {formatNightDateLabel(new Date(night.scheduledAt), night.region)} · {countdown.label} ·{" "}
                {getStatusLabel(rsvp.status)}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: "0.78rem", color: "rgba(255,255,255,0.58)" }}>
                Coordinated CS2 nights help everyone show up together. {getCountsLine(night)}.
                {cancelError ? ` ${cancelError}` : ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
              <Button
                component={RouterLink}
                to="/match-night"
                size="small"
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderColor: "rgba(232,163,61,0.5)",
                  color: ACCENT,
                  minWidth: 0,
                  "&:hover": { borderColor: ACCENT, backgroundColor: "rgba(232,163,61,0.08)" },
                }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="text"
                disabled={cancellingRsvpId === rsvp.id}
                onClick={handleCancel}
                sx={{
                  textTransform: "none",
                  color: "rgba(255,255,255,0.68)",
                  minWidth: 0,
                  "&:hover": { color: "rgba(255,255,255,0.9)", backgroundColor: "rgba(255,255,255,0.05)" },
                }}
              >
                {cancellingRsvpId === rsvp.id ? "Cancelling..." : "Cancel RSVP"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    );
  }

  if (!nextNight) return null;

  const isDismissed = dismissedNightId === nextNight.id;

  if (isDismissed) {
    return (
      <Fade in timeout={200}>
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            startIcon={<InfoOutlinedIcon fontSize="small" />}
            onClick={() => {
              clearDismissedNightId();
              setDismissedNightId(null);
            }}
            sx={{
              textTransform: "none",
              color: "rgba(255,255,255,0.35)",
              fontSize: "0.75rem",
              py: 0.35,
              px: 1,
              "&:hover": { color: "rgba(255,255,255,0.6)", backgroundColor: "rgba(255,255,255,0.04)" },
            }}
          >
            Show match night
          </Button>
        </Box>
      </Fade>
    );
  }

  return (
    <>
      <Fade in timeout={250}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.25, md: 1.4 },
            mb: 1,
            position: "relative",
            borderLeftWidth: 1,
            borderLeftStyle: "solid",
            borderLeftColor: ACCENT,
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              persistDismissedNightId(nextNight.id);
              setDismissedNightId(nextNight.id);
            }}
            aria-label="Dismiss"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              color: "rgba(255,255,255,0.35)",
              "&:hover": { color: "rgba(255,255,255,0.65)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ pr: 3 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "rgba(255,255,255,0.92)" }}>
              Match night: {formatNightDateLabel(new Date(nextNight.scheduledAt), nextNight.region)} · {countdown.label}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: "0.78rem", color: "rgba(255,255,255,0.58)" }}>
              Coordinated CS2 nights help everyone show up together. {getCountsLine(nextNight)}.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setRsvpDialogNight(nextNight)}
              sx={{
                backgroundColor: ACCENT,
                color: "#0B0D0F",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#F4B752", boxShadow: "none" },
              }}
            >
              Reserve
            </Button>
            <Button
              component={RouterLink}
              to="/match-night"
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderColor: "rgba(232,163,61,0.5)",
                color: ACCENT,
                "&:hover": { borderColor: ACCENT, backgroundColor: "rgba(232,163,61,0.08)" },
              }}
            >
              View all
            </Button>
          </Box>
        </Paper>
      </Fade>

      <MatchNightRsvpDialog
        open={Boolean(rsvpDialogNight)}
        night={rsvpDialogNight}
        basePath={basePath}
        onClose={() => setRsvpDialogNight(null)}
        onRsvpSuccess={handleRsvpSuccess}
      />
    </>
  );
}

LobbyMatchNightBanner.propTypes = {
  country: PropTypes.string,
};

